import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard, Boxes, KeyRound, Code2, Download, Copy, RefreshCw, LogOut,
  TrendingUp, CheckCircle2, Layers, AlertCircle, ExternalLink, FileDown, Package,
  Building2, Users, Tag, Save, UserPlus, Trash2, Image as ImageIcon, Plus, X,
  BadgeCheck, Globe, ShieldCheck,
} from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useAuth } from '../../App';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Use shared cookie-based axios (configured in App.js with withCredentials=true)
const partnerAxios = () => axios.create({ baseURL: API, withCredentials: true });

// Backend stores raw base64 (no prefix) in qr_code_base64 fields. Wrap before <img>.
const toDataUrl = (b64) => (!b64 ? '' : (b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`));

const TABS = [
  { id: 'overview', label: 'Vue d\'ensemble', icon: LayoutDashboard },
  { id: 'generate', label: 'Generer', icon: Boxes },
  { id: 'batches', label: 'Lots & IDs', icon: Layers },
  { id: 'organization', label: 'Organisation', icon: Building2 },
  { id: 'api', label: 'Cle API', icon: KeyRound },
  { id: 'docs', label: 'Documentation', icon: Code2 },
];

const PartnerDashboard = () => {
  const [tab, setTab] = useState('overview');
  const [partner, setPartner] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const loadAll = async () => {
    try {
      const ax = partnerAxios();
      const [me, st] = await Promise.all([
        ax.get('/partners/me'),
        ax.get('/partners/dashboard/stats'),
      ]);
      setPartner(me.data);
      setStats(st.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expiree');
        navigate('/login');
      } else if (err.response?.status === 403) {
        toast.error('Aucune organisation partenaire associee a ce compte');
        navigate('/dashboard');
      } else {
        toast.error('Erreur de chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-charcoal/60">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="bg-white border-b-4 border-charcoal sticky top-0 z-30" data-testid="partner-dashboard">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="bg-primary w-9 h-9 rounded-full border-2 border-charcoal flex items-center justify-center shadow-brutal">
              <span className="font-heading font-bold text-charcoal">T</span>
            </div>
            <div>
              <div className="font-heading font-bold text-charcoal">Tagora Partners</div>
              <div className="text-xs text-charcoal/60">{partner?.company_name}</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-charcoal/70 hover:text-charcoal hidden sm:inline-flex items-center gap-1">
              <ExternalLink className="w-4 h-4" /> Site public
            </Link>
            <button onClick={handleLogout} data-testid="partner-logout-btn" className="btn-outline px-3 py-2 text-sm inline-flex items-center gap-2">
              <LogOut className="w-4 h-4" /> Deconnexion
            </button>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`partner-tab-${id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-4 transition-colors whitespace-nowrap ${
                tab === id ? 'border-primary text-charcoal' : 'border-transparent text-charcoal/60 hover:text-charcoal'
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {tab === 'overview' && <OverviewTab partner={partner} stats={stats} onRefresh={loadAll} />}
        {tab === 'generate' && <GenerateTab onDone={loadAll} />}
        {tab === 'batches' && <BatchesTab />}
        {tab === 'organization' && <OrganizationTab partner={partner} onRefresh={loadAll} />}
        {tab === 'api' && <ApiKeyTab partner={partner} onRefresh={loadAll} />}
        {tab === 'docs' && <DocsTab partner={partner} />}
      </main>
    </div>
  );
};

// ============== OVERVIEW ==============
const OverviewTab = ({ partner, stats, onRefresh }) => {
  if (!stats) return null;
  const cards = [
    { label: 'Total genere', value: stats.total_generated, icon: Boxes, color: 'bg-primary/15 text-charcoal' },
    { label: 'Enroles par utilisateurs', value: stats.claimed, icon: CheckCircle2, color: 'bg-green-100 text-green-800' },
    { label: 'Non enroles', value: stats.unclaimed, icon: Package, color: 'bg-amber-100 text-amber-800' },
    { label: 'Taux d\'enrolement', value: `${stats.claim_rate}%`, icon: TrendingUp, color: 'bg-blue-100 text-blue-800' },
  ];
  const trend = (stats.claim_trend || []).map((d) => ({ ...d }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-charcoal">Bonjour, {partner?.contact_name}</h1>
          <p className="text-charcoal/60 text-sm">Voici l'activite de votre compte partenaire.</p>
        </div>
        <button onClick={onRefresh} className="btn-outline inline-flex items-center gap-2 text-sm" data-testid="partner-overview-refresh">
          <RefreshCw className="w-4 h-4" /> Rafraichir
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="card-brutal bg-white" data-testid={`partner-stat-${c.label.replace(/\s/g, '-').toLowerCase()}`}>
            <div className={`w-10 h-10 rounded-lg ${c.color} border-2 border-charcoal/10 flex items-center justify-center mb-3`}>
              <c.icon className="w-5 h-5" />
            </div>
            <div className="text-3xl font-bold font-heading text-charcoal">{c.value}</div>
            <div className="text-xs uppercase tracking-widest text-charcoal/60 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="card-brutal bg-white">
        <h2 className="font-heading font-bold text-xl text-charcoal mb-4">Enrolements sur 30 jours</h2>
        {trend.length === 0 ? (
          <div className="text-charcoal/60 text-sm py-12 text-center">Pas encore d'enrolements. Vos premiers clients verront leurs IDs ici.</div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="4 4" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#FFD93D" strokeWidth={3} dot={{ r: 4, fill: '#1F2937' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== GENERATE ==============
const GenerateTab = ({ onDone }) => {
  const [count, setCount] = useState(10);
  const [labelText, setLabelText] = useState('perdu ? Scannez moi');
  const [bgColor, setBgColor] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await partnerAxios().post('/partners/dashboard/generate', {
        count: Number(count),
        label_text: labelText || null,
        bg_color: bgColor || null,
        notes: notes || null,
      });
      setResult(data);
      toast.success(`${data.count} Tagora ID generes`);
      onDone?.();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === 'string' ? msg : 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async (batchId) => {
    const res = await partnerAxios().get(`/partners/dashboard/export.csv?batch_id=${batchId}`, { responseType: 'blob' });
    triggerDownload(res.data, `tagora-${batchId}.csv`);
  };
  const downloadZip = async (batchId) => {
    const res = await partnerAxios().get(`/partners/dashboard/export.zip?batch_id=${batchId}`, { responseType: 'blob' });
    triggerDownload(res.data, `tagora-qr-${batchId}.zip`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-bold text-3xl text-charcoal mb-1">Generer des Tagora ID</h1>
        <p className="text-charcoal/60 text-sm">Production immediate, illimitee et gratuite. Telechargeable en CSV ou ZIP de QR codes.</p>
      </div>

      <form onSubmit={submit} className="card-brutal bg-white grid lg:grid-cols-2 gap-6">
        <div>
          <label className="text-xs font-bold tracking-widest uppercase text-charcoal mb-2 block">Quantite</label>
          <input
            type="number"
            min="1"
            max="10000"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            className="input-brutal w-full"
            data-testid="partner-gen-count"
            required
          />
          <p className="text-xs text-charcoal/50 mt-1">1 a 10 000 par lot. Aucun quota mensuel.</p>
        </div>
        <div>
          <label className="text-xs font-bold tracking-widest uppercase text-charcoal mb-2 block">Label sticker</label>
          <input
            type="text"
            value={labelText}
            onChange={(e) => setLabelText(e.target.value)}
            className="input-brutal w-full"
            data-testid="partner-gen-label"
            placeholder="perdu ? Scannez moi"
          />
        </div>
        <div>
          <label className="text-xs font-bold tracking-widest uppercase text-charcoal mb-2 block">Couleur de fond QR (hex, optionnel)</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              placeholder="#FFD93D"
              className="input-brutal flex-1"
              data-testid="partner-gen-color"
            />
            {bgColor && <div className="w-12 rounded-lg border-2 border-charcoal/20" style={{ background: bgColor }} />}
          </div>
        </div>
        <div>
          <label className="text-xs font-bold tracking-widest uppercase text-charcoal mb-2 block">Note interne (lot)</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ex: lot janvier - bagages cuir"
            className="input-brutal w-full"
            data-testid="partner-gen-notes"
          />
        </div>
        <div className="lg:col-span-2 flex justify-end">
          <button type="submit" disabled={loading} data-testid="partner-gen-submit" className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
            <Boxes className="w-4 h-4" /> {loading ? 'Generation...' : `Generer ${count} ID`}
          </button>
        </div>
      </form>

      {result && (
        <div className="card-brutal bg-white" data-testid="partner-gen-result">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="font-heading font-bold text-xl text-charcoal">Lot {result.batch_id}</h2>
              <p className="text-charcoal/60 text-sm">{result.count} Tagora ID generes avec succes. Telechargez le CSV ou le ZIP de QR codes ci-contre, ou retrouvez-les dans l'onglet "Lots & IDs".</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => downloadCsv(result.batch_id)} className="btn-outline inline-flex items-center gap-2 text-sm" data-testid="partner-dl-csv">
                <FileDown className="w-4 h-4" /> CSV
              </button>
              <button onClick={() => downloadZip(result.batch_id)} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="partner-dl-zip">
                <Download className="w-4 h-4" /> ZIP QR
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== BATCHES ==============
const BatchesTab = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [qrModal, setQrModal] = useState(null); // { pingme_id, qr_code_base64, qr_code_url, claimed, ...}
  const [qrLoading, setQrLoading] = useState(false);
  const [miniMode, setMiniMode] = useState(false);
  const [miniData, setMiniData] = useState(null);
  const [miniLoading, setMiniLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await partnerAxios().get('/partners/dashboard/batches');
        setBatches(data.batches || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const open = async (batchId) => {
    setSelected(batchId);
    setItems([]);
    const { data } = await partnerAxios().get(`/partners/dashboard/ids?batch_id=${batchId}&limit=200`);
    setItems(data.items || []);
  };

  const downloadCsv = async (batchId) => {
    const res = await partnerAxios().get(`/partners/dashboard/export.csv?batch_id=${batchId}`, { responseType: 'blob' });
    triggerDownload(res.data, `tagora-${batchId}.csv`);
  };
  const downloadZip = async (batchId) => {
    const res = await partnerAxios().get(`/partners/dashboard/export.zip?batch_id=${batchId}`, { responseType: 'blob' });
    triggerDownload(res.data, `tagora-qr-${batchId}.zip`);
  };

  if (loading) return <div className="text-charcoal/60">Chargement...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-bold text-3xl text-charcoal mb-1">Lots & Tagora ID generes</h1>
        <p className="text-charcoal/60 text-sm">Historique des lots produits et detail des IDs.</p>
      </div>

      {batches.length === 0 ? (
        <div className="card-brutal bg-white text-center py-12 text-charcoal/60">Aucun lot pour le moment. Generez votre premier lot dans l'onglet "Generer".</div>
      ) : (
        <div className="card-brutal bg-white p-0 overflow-hidden">
          <table className="w-full" data-testid="partner-batches-table">
            <thead className="bg-background border-b-2 border-charcoal/20">
              <tr className="text-left text-xs font-bold uppercase tracking-widest text-charcoal/60">
                <th className="p-4">ID Lot</th>
                <th className="p-4">Date</th>
                <th className="p-4">Quantite</th>
                <th className="p-4">Enroles</th>
                <th className="p-4">Note</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.batch_id} className="border-b border-charcoal/10 hover:bg-background/50">
                  <td className="p-4 font-mono text-xs">{b.batch_id}</td>
                  <td className="p-4 text-sm text-charcoal/70">{b.created_at ? new Date(b.created_at).toLocaleString() : '-'}</td>
                  <td className="p-4 font-bold">{b.count}</td>
                  <td className="p-4">
                    <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-bold">{b.claimed} enroles</span>
                  </td>
                  <td className="p-4 text-sm text-charcoal/70">{b.notes || '-'}</td>
                  <td className="p-4 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => open(b.batch_id)} className="btn-outline text-xs px-3 py-1.5" data-testid={`partner-batch-view-${b.batch_id}`}>Voir</button>
                      <button onClick={() => downloadCsv(b.batch_id)} className="btn-outline text-xs px-3 py-1.5">CSV</button>
                      <button onClick={() => downloadZip(b.batch_id)} className="btn-primary text-xs px-3 py-1.5">ZIP</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="card-brutal bg-white" data-testid="partner-batch-detail">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-xl text-charcoal">Detail du lot {selected}</h2>
            <button onClick={() => setSelected(null)} className="text-sm text-charcoal/60 hover:text-charcoal">Fermer</button>
          </div>
          <p className="text-xs text-charcoal/60 mb-3">Cliquez sur un Tagora ID pour afficher son QR code.</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-96 overflow-y-auto">
            {items.map((it) => (
              <button
                key={it.pingme_id}
                type="button"
                onClick={async () => {
                  setMiniMode(false);
                  setMiniData(null);
                  setQrLoading(true);
                  setQrModal({ pingme_id: it.pingme_id, claimed: it.claimed, loading: true });
                  try {
                    const { data } = await partnerAxios().get(`/partners/dashboard/ids/${it.pingme_id}/qr`);
                    setQrModal(data);
                  } catch (err) {
                    toast.error('Impossible de charger le QR code');
                    setQrModal(null);
                  } finally {
                    setQrLoading(false);
                  }
                }}
                data-testid={`partner-id-card-${it.pingme_id}`}
                className="bg-background border-2 border-charcoal/10 rounded-xl p-3 text-left hover:border-charcoal hover:shadow-brutal-sm transition-all cursor-pointer"
              >
                <div className="font-mono text-xs text-charcoal break-all mb-1">{it.pingme_id}</div>
                <div className="flex items-center gap-2 text-xs">
                  {it.claimed ? (
                    <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full font-bold">Enrole</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full font-bold">Disponible</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {qrModal && (
        <div
          className="fixed inset-0 z-50 bg-charcoal/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => { setQrModal(null); setMiniMode(false); setMiniData(null); }}
          data-testid="partner-qr-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border-4 border-charcoal rounded-2xl shadow-brutal-xl max-w-md w-full p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs font-bold tracking-widest uppercase text-charcoal/60 mb-1">Tagora ID</div>
                <div className="font-mono text-base font-bold text-charcoal break-all">{qrModal.pingme_id}</div>
              </div>
              <button
                onClick={() => { setQrModal(null); setMiniMode(false); setMiniData(null); }}
                className="text-charcoal/60 hover:text-charcoal text-2xl leading-none"
                data-testid="partner-qr-modal-close"
                aria-label="Fermer"
              >
                ×
              </button>
            </div>

            {/* Toggle Standard / Mini */}
            <div className="grid grid-cols-2 gap-1 bg-background border-2 border-charcoal/10 rounded-xl p-1 mb-4">
              <button
                type="button"
                onClick={() => setMiniMode(false)}
                data-testid="partner-qr-mode-standard"
                className={`py-1.5 rounded-lg text-xs font-bold transition-colors ${!miniMode ? 'bg-primary text-charcoal' : 'text-charcoal/60'}`}
              >
                Standard
              </button>
              <button
                type="button"
                onClick={async () => {
                  setMiniMode(true);
                  if (!miniData) {
                    setMiniLoading(true);
                    try {
                      const { data } = await partnerAxios().get(`/partners/dashboard/ids/${qrModal.pingme_id}/mini-qr`);
                      setMiniData(data);
                    } catch (err) {
                      toast.error('Impossible de generer le mini QR');
                    } finally {
                      setMiniLoading(false);
                    }
                  }
                }}
                data-testid="partner-qr-mode-mini"
                className={`py-1.5 rounded-lg text-xs font-bold transition-colors ${miniMode ? 'bg-primary text-charcoal' : 'text-charcoal/60'}`}
              >
                Mini QR (lunettes)
              </button>
            </div>

            <div className="bg-background border-2 border-charcoal/10 rounded-xl p-6 flex items-center justify-center mb-4">
              {miniMode ? (
                miniLoading || !miniData?.mini_qr_base64 ? (
                  <div className="w-48 h-48 flex items-center justify-center text-sm text-charcoal/60">Generation...</div>
                ) : (
                  <img
                    src={toDataUrl(miniData.mini_qr_base64)}
                    alt={miniData.short_code}
                    className="w-48 h-48"
                    style={{ imageRendering: 'pixelated' }}
                    data-testid="partner-mini-qr-image"
                  />
                )
              ) : (
                qrLoading || !qrModal.qr_code_base64 ? (
                  <div className="w-48 h-48 flex items-center justify-center text-sm text-charcoal/60">Chargement...</div>
                ) : (
                  <img
                    src={toDataUrl(qrModal.qr_code_base64)}
                    alt={qrModal.pingme_id}
                    className="w-56 h-56"
                    data-testid="partner-qr-modal-image"
                  />
                )
              )}
            </div>

            {miniMode ? (
              <div className="space-y-2 text-sm">
                {miniData?.short_url && (
                  <>
                    <div className="flex justify-between gap-2">
                      <span className="text-charcoal/60 flex-shrink-0">Code court</span>
                      <span className="font-mono font-bold text-charcoal">{miniData.short_code}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-charcoal/60 flex-shrink-0">URL courte</span>
                      <a href={miniData.short_url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs break-all text-right">{miniData.short_url}</a>
                    </div>
                  </>
                )}
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 text-xs text-amber-900 mt-3">
                  <strong>Conseils d'impression :</strong> taille minimum recommandee {miniData?.recommended_min_size_mm || 7}mm × {miniData?.recommended_min_size_mm || 7}mm. Imprimez en 600+ DPI sur fond clair, sans recouvrement. Le QR utilise une correction d'erreur maximale (15% de tolerance aux rayures), ideal pour une branche de lunettes.
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-charcoal/60">Statut</span>
                  {qrModal.claimed ? (
                    <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full font-bold text-xs">Enrole</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full font-bold text-xs">Disponible</span>
                  )}
                </div>
                {qrModal.batch_id && (
                  <div className="flex justify-between">
                    <span className="text-charcoal/60">Lot</span>
                    <span className="font-mono text-xs">{qrModal.batch_id}</span>
                  </div>
                )}
                {qrModal.qr_code_url && (
                  <div className="flex justify-between gap-2">
                    <span className="text-charcoal/60 flex-shrink-0">URL</span>
                    <a href={qrModal.qr_code_url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs break-all text-right">{qrModal.qr_code_url}</a>
                  </div>
                )}
              </div>
            )}

            {miniMode ? (
              miniData?.mini_qr_base64 && (
                <a
                  href={toDataUrl(miniData.mini_qr_base64)}
                  download={`${qrModal.pingme_id}-mini.png`}
                  className="btn-outline w-full mt-4 inline-flex items-center justify-center gap-2 text-sm"
                  data-testid="partner-mini-qr-download"
                >
                  <Download className="w-4 h-4" /> Telecharger le mini PNG
                </a>
              )
            ) : (
              qrModal.qr_code_base64 && (
                <a
                  href={toDataUrl(qrModal.qr_code_base64)}
                  download={`${qrModal.pingme_id}.png`}
                  className="btn-outline w-full mt-4 inline-flex items-center justify-center gap-2 text-sm"
                  data-testid="partner-qr-modal-download"
                >
                  <Download className="w-4 h-4" /> Telecharger le PNG
                </a>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============== ORGANIZATION (Branding / Members / Categories) ==============
const resolveImg = (u) => (u && u.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const DomainVerificationPanel = ({ orgId }) => {
  const [state, setState] = useState(null); // {claimed, verified, domain, token, instructions, verified_at, last_check_error, ...}
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await partnerAxios().get(`/organizations/${orgId}/domain-verification`);
      setState(data);
      if (data?.domain) setDomain(data.domain);
    } catch (err) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, [orgId]); // eslint-disable-line

  const claim = async (e) => {
    e?.preventDefault?.();
    if (!domain.trim()) return;
    setWorking(true);
    try {
      const { data } = await partnerAxios().post(`/organizations/${orgId}/domain-verification/claim`, { domain: domain.trim() });
      setState({ ...data, claimed: true });
      toast.success('Domaine reclame, placez le token sur votre site puis verifiez.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setWorking(false); }
  };

  const check = async () => {
    setWorking(true);
    try {
      const { data } = await partnerAxios().post(`/organizations/${orgId}/domain-verification/check`);
      if (data.verified) {
        toast.success(`Domaine ${data.domain} verifie !`);
      } else {
        toast.error(data.error || 'Verification echouee');
      }
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setWorking(false); }
  };

  const clearClaim = async () => {
    if (!window.confirm('Supprimer la reclamation et la verification du domaine ?')) return;
    try {
      await partnerAxios().delete(`/organizations/${orgId}/domain-verification`);
      setState({ claimed: false });
      setDomain('');
      toast.success('Verification supprimee');
    } catch (err) { toast.error('Erreur'); }
  };

  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); toast.success('Copie'); } catch {}
  };

  if (loading) return <div className="text-charcoal/60">Chargement...</div>;

  return (
    <div className="space-y-5 max-w-2xl" data-testid="partner-org-domain">
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-7 h-7 text-charcoal/70 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-bold text-charcoal text-lg leading-tight">Verifier la propriete de votre site web</h3>
          <p className="text-sm text-charcoal/65 mt-1">
            Prouvez que vous controlez le domaine de votre marque. Une fois verifie, un badge <em>Site verifie</em> s'affiche aupres de votre nom sur la page publique de chaque Tagora ID que vous generez.
          </p>
        </div>
      </div>

      <form onSubmit={claim} className="bg-background border-2 border-charcoal/10 rounded-xl p-4 space-y-3">
        <label className="text-xs font-bold tracking-widest uppercase text-charcoal mb-1 block">Domaine</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="acme.com"
            className="input-brutal flex-1"
            data-testid="partner-org-domain-input"
            disabled={state?.verified}
          />
          {!state?.claimed && (
            <button type="submit" disabled={working} className="btn-primary" data-testid="partner-org-domain-claim">
              {working ? '...' : 'Reclamer'}
            </button>
          )}
        </div>
        <p className="text-[11px] text-charcoal/50">Sans http:// ni www. Exemple: <code>acme.com</code></p>
      </form>

      {state?.claimed && (
        <>
          <div className={`rounded-xl border-2 p-4 ${state.verified ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300'}`}>
            <div className="flex items-start gap-3">
              {state.verified ? (
                <BadgeCheck className="w-6 h-6 text-green-700 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-6 h-6 text-amber-700 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className={`font-bold ${state.verified ? 'text-green-900' : 'text-amber-900'}`}>
                  {state.verified ? `Verifie : ${state.domain}` : `En attente de verification : ${state.domain}`}
                </p>
                {state.verified_at && (
                  <p className="text-xs text-charcoal/60 mt-0.5">Verifie le {new Date(state.verified_at).toLocaleString('fr-FR')}</p>
                )}
                {!state.verified && state.last_check_error && (
                  <p className="text-xs text-amber-900/80 mt-1">Derniere erreur : {state.last_check_error}</p>
                )}
              </div>
            </div>
          </div>

          {!state.verified && state.instructions && (
            <div className="space-y-3">
              <p className="text-sm text-charcoal/75">Choisissez <strong>une</strong> des deux methodes :</p>

              <div className="bg-white border-2 border-charcoal/15 rounded-xl p-4">
                <p className="text-xs font-bold tracking-widest uppercase text-charcoal/60 mb-2">Methode 1 : balise meta</p>
                <p className="text-xs text-charcoal/65 mb-2">Ajoutez cette ligne dans le <code>&lt;head&gt;</code> de votre page d'accueil <code>https://{state.domain}/</code> :</p>
                <div className="flex gap-2">
                  <code className="flex-1 bg-charcoal text-primary font-mono text-xs p-3 rounded-lg break-all">{state.instructions.meta_tag}</code>
                  <button onClick={() => copy(state.instructions.meta_tag)} className="btn-outline px-3"><Copy className="w-4 h-4" /></button>
                </div>
              </div>

              <div className="bg-white border-2 border-charcoal/15 rounded-xl p-4">
                <p className="text-xs font-bold tracking-widest uppercase text-charcoal/60 mb-2">Methode 2 : fichier .well-known</p>
                <p className="text-xs text-charcoal/65 mb-2">Servez le fichier suivant a l'URL publique :</p>
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-4 h-4 text-charcoal/50" />
                  <code className="text-xs font-mono break-all">{state.instructions.well_known_url}</code>
                </div>
                <p className="text-xs text-charcoal/65 mb-1">Avec le contenu (exact, sans espaces) :</p>
                <div className="flex gap-2">
                  <code className="flex-1 bg-charcoal text-primary font-mono text-xs p-3 rounded-lg break-all">{state.instructions.well_known_content}</code>
                  <button onClick={() => copy(state.instructions.well_known_content)} className="btn-outline px-3"><Copy className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center gap-3">
            <button onClick={clearClaim} className="text-danger text-sm hover:underline" data-testid="partner-org-domain-clear">
              Supprimer la reclamation
            </button>
            <button onClick={check} disabled={working} className="btn-primary inline-flex items-center gap-2" data-testid="partner-org-domain-check">
              <RefreshCw className={`w-4 h-4 ${working ? 'animate-spin' : ''}`} /> {working ? 'Verification...' : (state.verified ? 'Re-verifier' : 'Lancer la verification')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};



const OrganizationTab = ({ partner, onRefresh }) => {
  const [subTab, setSubTab] = useState('branding');
  const orgId = partner?.org_id;
  const [branding, setBranding] = useState(partner?.branding || {});
  const [admins, setAdmins] = useState([]);
  const [categories, setCategories] = useState(partner?.categories || []);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newAdmin, setNewAdmin] = useState('');
  const [newCatKey, setNewCatKey] = useState('');
  const [newCatLabel, setNewCatLabel] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await partnerAxios().get(`/organizations/${orgId}`);
        if (cancel) return;
        setBranding(data.branding || {});
        setAdmins(data.admins || []);
        setCategories(data.categories || []);
      } catch (err) {
        toast.error('Impossible de charger l\'organisation');
      } finally {
        if (!cancel) setLoadingOrg(false);
      }
    })();
    return () => { cancel = true; };
  }, [orgId]);

  const uploadImage = async (file, field) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await partnerAxios().post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBranding({ ...branding, [field]: res.data.url });
    } catch { toast.error('Upload impossible'); }
    finally { setUploading(false); }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      const res = await partnerAxios().put(`/organizations/${orgId}/branding`, branding);
      setBranding(res.data.branding);
      toast.success('Branding mis a jour');
      onRefresh && onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.trim()) return;
    try {
      const res = await partnerAxios().post(`/organizations/${orgId}/admins`, { email: newAdmin.trim() });
      setAdmins([...admins, res.data.admin]);
      setNewAdmin('');
      toast.success('Admin ajoute');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const removeAdmin = async (userId, email) => {
    if (!window.confirm(`Retirer ${email} des admins ?`)) return;
    try {
      await partnerAxios().delete(`/organizations/${orgId}/admins/${userId}`);
      setAdmins(admins.filter((a) => a.user_id !== userId));
      toast.success('Admin retire');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const addCategory = () => {
    const key = newCatKey.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    const label = newCatLabel.trim();
    if (!key || !label) return toast.error('Cle et libelle requis');
    if (categories.some((c) => c.key === key)) return toast.error('Cle deja utilisee');
    setCategories([...categories, { key, label }]);
    setNewCatKey('');
    setNewCatLabel('');
  };
  const removeCategory = (key) => setCategories(categories.filter((c) => c.key !== key));
  const saveCategories = async () => {
    setSaving(true);
    try {
      const res = await partnerAxios().put(`/organizations/${orgId}/categories`, { categories });
      setCategories(res.data.categories);
      toast.success('Categories enregistrees');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  if (loadingOrg) return <div className="text-charcoal/60">Chargement de l'organisation...</div>;

  const SUB_TABS = [
    { id: 'branding', label: 'Branding', icon: ImageIcon },
    { id: 'domain', label: 'Vérification', icon: ShieldCheck },
    { id: 'members', label: 'Membres', icon: Users },
    { id: 'categories', label: 'Categories', icon: Tag },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-bold text-3xl text-charcoal mb-1">Organisation</h1>
        <p className="text-charcoal/60 text-sm">Personnalisez l'identite, les membres et les categories de <strong>{partner?.company_name}</strong>.</p>
      </div>

      <div className="card-brutal bg-white p-0 overflow-hidden">
        <div className="flex gap-1 border-b-2 border-charcoal/10 bg-background px-2">
          {SUB_TABS.map(({ id, label, icon: Ic }) => (
            <button
              key={id}
              onClick={() => setSubTab(id)}
              data-testid={`partner-org-subtab-${id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-4 transition-colors -mb-0.5 ${
                subTab === id ? 'border-primary text-charcoal' : 'border-transparent text-charcoal/60 hover:text-charcoal'
              }`}
            >
              <Ic className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {subTab === 'branding' && (
            <div className="space-y-4 max-w-2xl">
              <div>
                <label className="text-xs font-bold tracking-widest uppercase text-charcoal mb-2 block">Nom affiche</label>
                <input className="input-brutal w-full" value={branding.display_name || ''} onChange={(e) => setBranding({ ...branding, display_name: e.target.value })} data-testid="partner-org-display-name" />
              </div>
              <div>
                <label className="text-xs font-bold tracking-widest uppercase text-charcoal mb-2 block">Tagline</label>
                <input className="input-brutal w-full" value={branding.tagline || ''} onChange={(e) => setBranding({ ...branding, tagline: e.target.value })} data-testid="partner-org-tagline" />
              </div>
              <div>
                <label className="text-xs font-bold tracking-widest uppercase text-charcoal mb-2 block">Site web</label>
                <input className="input-brutal w-full" value={branding.website_url || ''} onChange={(e) => setBranding({ ...branding, website_url: e.target.value })} placeholder="https://" data-testid="partner-org-website" />
              </div>
              <div>
                <label className="text-xs font-bold tracking-widest uppercase text-charcoal mb-2 block">Couleur principale</label>
                <div className="flex items-center gap-3">
                  <input type="color" className="w-12 h-10 rounded-lg border-2 border-charcoal/20 cursor-pointer" value={branding.primary_color || '#FFD93D'} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} />
                  <input className="input-brutal flex-1" value={branding.primary_color || ''} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} placeholder="#FFD93D" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { field: 'logo_url', label: 'Logo (carre)' },
                  { field: 'cover_image_url', label: 'Image de couverture' },
                ].map(({ field, label }) => (
                  <div key={field}>
                    <label className="text-xs font-bold tracking-widest uppercase text-charcoal mb-2 block">{label}</label>
                    <div className="flex items-start gap-3">
                      <div className="w-20 h-20 rounded-lg border-2 border-charcoal/15 overflow-hidden bg-background flex items-center justify-center shrink-0">
                        {branding[field] ? (
                          <img src={resolveImg(branding[field])} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon className="w-6 h-6 text-charcoal/30" />
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <label className="btn-outline text-xs px-3 py-1.5 cursor-pointer inline-flex items-center gap-1" data-testid={`partner-org-upload-${field}`}>
                          {uploading ? '...' : 'Choisir'}
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], field)} />
                        </label>
                        {branding[field] && (
                          <button type="button" onClick={() => setBranding({ ...branding, [field]: '' })} className="text-xs text-danger hover:underline ml-2">Retirer</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={branding.embed_qr_logo !== false} onChange={(e) => setBranding({ ...branding, embed_qr_logo: e.target.checked })} data-testid="partner-org-embed-logo" />
                <span className="text-sm text-charcoal/80">Embarquer le logo au centre des QR codes generes</span>
              </label>
              <div className="flex justify-end pt-4">
                <button onClick={saveBranding} disabled={saving} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="partner-org-save-branding">
                  <Save className="w-4 h-4" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          )}

          {subTab === 'domain' && <DomainVerificationPanel orgId={orgId} />}

          {subTab === 'members' && (
            <div className="space-y-4 max-w-2xl">
              <form onSubmit={addAdmin} className="flex gap-2" data-testid="partner-org-add-admin-form">
                <input className="input-brutal flex-1" type="email" placeholder="Email du nouvel admin" value={newAdmin} onChange={(e) => setNewAdmin(e.target.value)} data-testid="partner-org-new-admin-email" />
                <button type="submit" className="btn-primary inline-flex items-center gap-2"><UserPlus className="w-4 h-4" /> Ajouter</button>
              </form>
              <div className="space-y-2">
                {admins.length === 0 ? (
                  <div className="text-charcoal/60 text-sm text-center py-6">Aucun admin pour l'instant.</div>
                ) : admins.map((a) => (
                  <div key={a.user_id} className="flex items-center justify-between bg-background border-2 border-charcoal/10 rounded-xl px-4 py-3" data-testid={`partner-org-admin-${a.user_id}`}>
                    <div>
                      <div className="font-bold text-charcoal text-sm">{a.name || a.email}</div>
                      <div className="text-xs text-charcoal/60">{a.email}</div>
                    </div>
                    <button onClick={() => removeAdmin(a.user_id, a.email)} className="text-danger hover:text-danger/80 p-1" disabled={admins.length === 1} title={admins.length === 1 ? 'Au moins un admin requis' : ''}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {subTab === 'categories' && (
            <div className="space-y-4 max-w-2xl">
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                <input className="input-brutal" placeholder="Cle (ex: bagages)" value={newCatKey} onChange={(e) => setNewCatKey(e.target.value)} data-testid="partner-org-new-cat-key" />
                <input className="input-brutal" placeholder="Libelle visible (ex: Bagages)" value={newCatLabel} onChange={(e) => setNewCatLabel(e.target.value)} data-testid="partner-org-new-cat-label" />
                <button onClick={addCategory} className="btn-outline inline-flex items-center gap-1"><Plus className="w-4 h-4" /></button>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.length === 0 ? (
                  <div className="text-charcoal/60 text-sm w-full text-center py-6">Aucune categorie. Ajoutez-en pour structurer les PingMes des clients finaux.</div>
                ) : categories.map((c) => (
                  <div key={c.key} className="flex items-center gap-2 bg-background border-2 border-charcoal/10 rounded-full pl-3 pr-1 py-1 text-sm" data-testid={`partner-org-cat-${c.key}`}>
                    <span className="font-bold">{c.label}</span>
                    <span className="text-xs text-charcoal/40 font-mono">{c.key}</span>
                    <button onClick={() => removeCategory(c.key)} className="p-1 hover:bg-danger/10 rounded-full"><X className="w-3 h-3 text-danger" /></button>
                  </div>
                ))}
              </div>
              <div className="flex justify-end pt-2">
                <button onClick={saveCategories} disabled={saving} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="partner-org-save-categories">
                  <Save className="w-4 h-4" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ============== API KEY ==============
const ApiKeyTab = ({ partner, onRefresh }) => {
  const [newKey, setNewKey] = useState(null);
  const [loading, setLoading] = useState(false);

  const regenerate = async () => {
    if (!window.confirm('Cela invalidera votre cle actuelle. Continuer ?')) return;
    setLoading(true);
    try {
      const { data } = await partnerAxios().post('/partners/api-key/regenerate');
      setNewKey(data.api_key);
      toast.success('Nouvelle cle API generee');
      onRefresh?.();
    } catch (err) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading font-bold text-3xl text-charcoal mb-1">Cle API</h1>
        <p className="text-charcoal/60 text-sm">Utilisez votre cle pour appeler l'API REST depuis vos systemes.</p>
      </div>

      <div className="card-brutal bg-white">
        <label className="text-xs font-bold tracking-widest uppercase text-charcoal/60 mb-2 block">Prefixe de la cle (lecture seule)</label>
        <code className="block bg-charcoal text-primary font-mono text-sm p-4 rounded-lg" data-testid="partner-api-prefix">
          {partner?.api_key_prefix}***
        </code>
        <p className="text-xs text-charcoal/50 mt-2">La cle complete n'est affichee qu'a la creation ou apres regeneration. Elle est stockee en clair pour authentifier les requetes.</p>

        <div className="mt-6 pt-6 border-t-2 border-charcoal/10">
          <h3 className="font-bold text-charcoal mb-2">Regenerer la cle</h3>
          <p className="text-sm text-charcoal/60 mb-4">L'ancienne cle sera immediatement invalidee. Tous les systemes utilisant l'ancienne cle devront etre mis a jour.</p>
          <button onClick={regenerate} disabled={loading} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="partner-api-regenerate">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Regenerer la cle API
          </button>
        </div>

        {newKey && (
          <div className="mt-6 bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4">
            <div className="flex gap-3 mb-3">
              <AlertCircle className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-900"><strong>Sauvegardez cette nouvelle cle.</strong> Elle ne sera plus affichee.</div>
            </div>
            <div className="flex gap-2">
              <code className="flex-1 bg-charcoal text-primary font-mono text-sm p-3 rounded-lg break-all" data-testid="partner-api-newkey-value">{newKey}</code>
              <button onClick={() => { navigator.clipboard.writeText(newKey); toast.success('Copie'); }} className="btn-outline px-3"><Copy className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      <div className="card-brutal bg-white">
        <h3 className="font-bold text-charcoal mb-2">Securite</h3>
        <ul className="text-sm text-charcoal/70 space-y-2">
          <li>• Ne partagez jamais votre cle API publiquement (front-end, GitHub, etc.)</li>
          <li>• Utilisez-la uniquement depuis vos serveurs de production</li>
          <li>• En cas de compromission, regenerez-la immediatement</li>
        </ul>
      </div>
    </div>
  );
};

// ============== DOCS ==============
const DocsTab = ({ partner }) => {
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const exampleKey = partner?.api_key_prefix ? `${partner.api_key_prefix}...` : 'ptr_...';

  const examples = useMemo(() => ([
    {
      title: 'Generer des Tagora ID',
      method: 'POST',
      path: '/partners/v1/generate',
      body: `{\n  "count": 100,\n  "label_text": "perdu ? Scannez moi",\n  "bg_color": "#FFD93D",\n  "notes": "lot mars - tag bagages"\n}`,
      response: `{\n  "batch_id": "batch_abc123",\n  "count": 100,\n  "generated": [\n    { "pingme_id": "TGR-...-...-...-...", "qr_code_url": "https://tagora.fr/ping/TGR-...", "qr_code_base64": "data:image/png;base64,..." },\n    ...\n  ]\n}`,
    },
    { title: 'Lister les IDs', method: 'GET', path: '/partners/v1/ids?skip=0&limit=100&batch_id=batch_xxx', body: null, response: '{ "items": [...], "total": 1234 }' },
    { title: 'Lister les lots', method: 'GET', path: '/partners/v1/batches', body: null, response: '{ "batches": [{ "batch_id": "...", "count": N, "claimed": M }] }' },
    { title: 'Export CSV', method: 'GET', path: '/partners/v1/export.csv?batch_id=optional', body: null, response: 'text/csv attachment' },
    { title: 'Export ZIP des QR codes', method: 'GET', path: '/partners/v1/export.zip?batch_id=optional', body: null, response: 'application/zip attachment' },
  ]), []);

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-heading font-bold text-3xl text-charcoal mb-1">Documentation API</h1>
        <p className="text-charcoal/60 text-sm">Integrez la generation Tagora ID directement dans vos systemes.</p>
      </div>

      <div className="card-brutal bg-white">
        <h3 className="font-bold text-charcoal mb-3">Authentification</h3>
        <p className="text-sm text-charcoal/70 mb-3">Toutes les requetes API doivent inclure votre cle dans l'en-tete <code className="bg-charcoal/5 px-1 rounded">X-Partner-Api-Key</code>.</p>
        <pre className="bg-charcoal text-primary text-xs p-4 rounded-lg overflow-x-auto" data-testid="partner-docs-auth"><code>{`curl -X POST '${apiBase}/partners/v1/generate' \\\n  -H 'X-Partner-Api-Key: ${exampleKey}' \\\n  -H 'Content-Type: application/json' \\\n  -d '{"count": 10}'`}</code></pre>
      </div>

      <div className="card-brutal bg-white">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <h3 className="font-bold text-charcoal mb-1">Tester l'API en direct (Swagger)</h3>
            <p className="text-sm text-charcoal/70">Console interactive pour essayer chaque endpoint sans ecrire de code. Cliquez "Authorize" et collez votre cle API dans le champ <code className="bg-charcoal/5 px-1 rounded">X-Partner-Api-Key</code>.</p>
          </div>
          <a
            href={`${apiBase}/docs`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2 shrink-0"
            data-testid="partner-docs-swagger-open"
          >
            <ExternalLink className="w-4 h-4" /> Ouvrir Swagger
          </a>
        </div>
        {/* Mobile: too narrow for Swagger UI - show CTA card instead */}
        <div className="md:hidden rounded-lg border-2 border-dashed border-charcoal/20 bg-surface-alt/40 p-4 text-center">
          <p className="text-sm text-charcoal/70 mb-3">Swagger UI est optimisee pour grand ecran. Ouvrez-la en plein ecran pour une meilleure experience.</p>
          <a
            href={`${apiBase}/docs`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="partner-docs-swagger-open-mobile"
          >
            <ExternalLink className="w-4 h-4" /> Ouvrir en plein ecran
          </a>
        </div>
        {/* Desktop: embedded iframe */}
        <div className="hidden md:block rounded-lg border-2 border-charcoal/10 overflow-hidden bg-white">
          <iframe
            src={`${apiBase}/docs`}
            title="Tagora API - Swagger UI"
            className="w-full"
            style={{ height: 600, border: 0 }}
            data-testid="partner-docs-swagger-iframe"
          />
        </div>
      </div>

      {examples.map((ex) => (
        <div key={ex.title} className="card-brutal bg-white">
          <div className="flex items-center gap-3 mb-3">
            <span className={`px-2 py-1 text-xs font-bold rounded ${ex.method === 'POST' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>{ex.method}</span>
            <code className="font-mono text-sm text-charcoal/80">{ex.path}</code>
          </div>
          <h3 className="font-bold text-charcoal mb-3">{ex.title}</h3>
          {ex.body && (<>
            <div className="text-xs uppercase tracking-widest text-charcoal/50 mb-1">Body</div>
            <pre className="bg-background border-2 border-charcoal/10 text-xs p-3 rounded-lg overflow-x-auto mb-3"><code>{ex.body}</code></pre>
          </>)}
          <div className="text-xs uppercase tracking-widest text-charcoal/50 mb-1">Reponse</div>
          <pre className="bg-charcoal text-primary text-xs p-3 rounded-lg overflow-x-auto"><code>{ex.response}</code></pre>
        </div>
      ))}

      <div className="card-brutal bg-white">
        <h3 className="font-bold text-charcoal mb-2">Comportement d'enrolement</h3>
        <p className="text-sm text-charcoal/70">Lorsqu'un utilisateur final scanne pour la premiere fois un QR genere, il est invite a creer un compte (s'il n'en a pas). Le Tagora ID lui est alors attribue automatiquement et il pourra le personnaliser depuis son dashboard. Vous gardez la trace du lot d'origine et du taux d'enrolement.</p>
      </div>
    </div>
  );
};

// helpers
const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default PartnerDashboard;
